import { Injectable } from '@angular/core';
import { HttpClient, HttpEvent, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import {
  QuoteAttachmentDownloadEntry,
  QuoteAttachmentPublicResponse,
  QuoteAttachmentResponse
} from '../models/quote-attachment.model';

/**
 * Adjuntos de cotización (fichas técnicas, contratos, certificaciones).
 * Endpoints autenticados (Sales Rep+, businessId obligatorio) + portal público por token.
 */
@Injectable({
  providedIn: 'root'
})
export class QuoteAttachmentApiService {
  private readonly apiUrl = `${environment.apiUrl}api`;

  constructor(private http: HttpClient) { }

  // POST /api/quotes/{id}/attachments — multipart/form-data campo "file".
  // Máx 10 MB; MIME restringido (otro → 400 {message}). Emite eventos de progreso de subida.
  uploadAttachment(quoteId: number, businessId: number, file: File): Observable<HttpEvent<QuoteAttachmentResponse>> {
    const params = new HttpParams().set('businessId', businessId.toString());
    const formData = new FormData();
    formData.append('file', file, file.name);
    return this.http.post<QuoteAttachmentResponse>(`${this.apiUrl}/quotes/${quoteId}/attachments`, formData, {
      params,
      reportProgress: true,
      observe: 'events'
    });
  }

  // GET /api/quotes/{id}/attachments — array no paginado; downloadCount = PUBLIC + CRM.
  getAttachments(quoteId: number, businessId: number): Observable<QuoteAttachmentResponse[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteAttachmentResponse[]>(`${this.apiUrl}/quotes/${quoteId}/attachments`, { params });
  }

  // DELETE /api/quotes/{id}/attachments/{attachmentId} — 204.
  // (El archivo físico se conserva si otra versión/duplicado lo referencia.)
  deleteAttachment(quoteId: number, attachmentId: number, businessId: number): Observable<void> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.delete<void>(`${this.apiUrl}/quotes/${quoteId}/attachments/${attachmentId}`, { params });
  }

  // GET /api/quotes/{id}/attachments/{attachmentId}/download — stream binario.
  // Registra tracking via=CRM en el backend.
  downloadAttachment(quoteId: number, attachmentId: number, businessId: number): Observable<Blob> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get(`${this.apiUrl}/quotes/${quoteId}/attachments/${attachmentId}/download`, {
      params,
      responseType: 'blob'
    });
  }

  // GET /api/quotes/{id}/attachments/{attachmentId}/downloads — historial, más reciente primero.
  getAttachmentDownloads(quoteId: number, attachmentId: number, businessId: number): Observable<QuoteAttachmentDownloadEntry[]> {
    const params = new HttpParams().set('businessId', businessId.toString());
    return this.http.get<QuoteAttachmentDownloadEntry[]>(
      `${this.apiUrl}/quotes/${quoteId}/attachments/${attachmentId}/downloads`,
      { params }
    );
  }

  // ---- Portal público (sin JWT) ----

  // GET /api/quotes/public/{token}/attachments — 404 si el token no es válido/compartible.
  getPublicAttachments(token: string): Observable<QuoteAttachmentPublicResponse[]> {
    return this.http.get<QuoteAttachmentPublicResponse[]>(`${this.apiUrl}/quotes/public/${token}/attachments`);
  }

  // GET /api/quotes/public/{token}/attachments/{attachmentId}/download — stream binario.
  // Registra tracking PUBLIC con IP/user-agent.
  downloadPublicAttachment(token: string, attachmentId: number): Observable<Blob> {
    return this.http.get(`${this.apiUrl}/quotes/public/${token}/attachments/${attachmentId}/download`, {
      responseType: 'blob'
    });
  }
}
